import React, { useState, useEffect } from "react";
import LoadingOverlay from "./LoadingOverlay";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";

const GlobalUpdate: React.FC = () => {
  interface Note {
    itemNoteTypeId: string;
    note: string;
    staffOnly: boolean;
  }

  interface itemLocation {
    id: string;
    name: string;
  }

  interface itemMType {
    id: string;
    name: string;
  }

  interface effCallNumComp {
    callNumber: string;
    suffix: string;
  }

  interface Item {
    id: string;
    hrid: string;
    barcode: string;
    callNumber: string;
    effectiveCallNumberComponents: effCallNumComp;
    itemLevelCallNumber: string;
    itemLevelCallNumberSuffix: string;
    notes: Note[];
    holdingsRecordId: string;
    permanentLocation: itemLocation;
    effectiveLocation: itemLocation;
    materialType: itemMType;
    copyNumber: string;
    volume: string;
  }

  interface NoteList {
    itemId: string;
    noteTypeId: string;
    note: string;
  }

  interface LocalMaterialType {
    holdingID: string;
    mTypeID: string;
    mTypeName: string;
  }

  const [value, setValue] = useState<string>("");
  const [token, setToken] = useState<any>("");
  const [valueCallNumber, setValueCallNumber] = useState<string>("");
  const [valueSuffixCallNumber, setValueSuffixCallNumber] =
    useState<string>("Default");
  const [valueNotes, setValueNotes] = useState<NoteList[]>([]);
  const [instanceData, setInstanceData] = useState<any[]>([]);
  const [itemData, setItemData] = useState<any[]>([]);
  const [holdingData, setHoldingData] = useState<any[]>([]);
  const [holdingDetails, setHoldingDetails] = useState<any[]>([]);
  const [itemDetails, setItemDetails] = useState<Item[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [mTypes, setMTypes] = useState<any[]>([]);
  const [localMaterialType, setLocalMaterialType] = useState<
    LocalMaterialType[]
  >([]);

  const [error, setError] = useState<any[]>([]);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const headers = {
    "Content-Type": "application/json",
    "x-okapi-tenant": "dctuae",
    "x-okapi-token": token,
  };

  const handleCheck = async () => {
    setIsLoading(true);
    fetchLocations();
    fetchMTypes();

    setValueCallNumber("");
    setValueSuffixCallNumber("Default");
    setValueNotes([]);
    setInstanceData([]);
    setItemData([]);
    setHoldingData([]);
    setHoldingDetails([]);
    setItemDetails([]);
    setLocations([]);
    setError([]);
    setMessage("");

    await fetch(
      `https://api01-v2-uae.ils.medad.com/search/instances?expandAll=true&limit=30&query=(hrid=="${value}")`,
      { headers }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        if (data.totalRecords === 0) {
          setMessage("Instance not found");
        }
        const instance = data.instances
          .map((instance: any) => {
            return { title: instance.indexTitle };
          })
          .flat();

        // Preparing Items data
        const items = data.instances
          .map(
            (instance: any) =>
              instance.items?.map((item: any) => ({
                id: item.id,
                hrid: item.hrid,
                barcode: item.barcode,
                callNumber:
                  item.effectiveCallNumberComponents?.callNumber || "N/A",
                suffix: item.effectiveCallNumberComponents?.suffix || "N/A",
              })) || []
          )
          .flat()
          .sort((a: any, b: any) => a.hrid.localeCompare(b.hrid)); // Sorting items by hrid

        // Preparing Holdings data
        const holdings = data.instances
          .map(
            (instance: any) =>
              instance.holdings?.map((holding: any) => ({
                id: holding.id,
                hrid: holding.hrid,
              })) || []
          )
          .flat()
          .sort((a: any, b: any) => a.hrid.localeCompare(b.hrid)); // Sorting holdings by hrid

        // Updating state with sorted data
        setInstanceData(instance);
        setItemDetails([]);
        setItemData(items);
        items.forEach((item: any) => handleItemsDetails(item.id));

        setHoldingDetails([]);
        setHoldingData(holdings);
        holdings.forEach((holding: any) => handleHoldingDetails(holding.id));

        setError([]);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setError((prevErrors) => [...prevErrors, error.message]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleHoldingDetails = async (id: string) => {
    setIsLoading(true);
    await fetch(
      `https://api01-v2-uae.ils.medad.com/holdings-storage/holdings/${id}`,
      {
        headers,
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        const holdingsRecords = data;
        if (value === holdingsRecords.hrid) {
          setValueCallNumber(holdingsRecords.callNumber);
          setValueSuffixCallNumber(holdingsRecords.callNumberSuffix);
        }
        // setHoldingDetails((prevRecords) => [...prevRecords, holdingsRecords]);
        setHoldingDetails((prevRecords) => {
          const updatedRecords = [...prevRecords, holdingsRecords];
          return updatedRecords.sort((a, b) => a.hrid.localeCompare(b.hrid));
        });
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setError((prevErrors) => [...prevErrors, error.message]);
      });
    setIsLoading(false);
  };
  const handleItemsDetails = async (id: string) => {
    setIsLoading(true);
    await fetch(`https://api01-v2-uae.ils.medad.com/inventory/items/${id}`, {
      headers,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        const itemsRecords: Item = data;
        // setItemDetails((prevRecords) => [...prevRecords, itemsRecords]);
        setItemDetails((prevRecords) => {
          const updatedRecords = [...prevRecords, itemsRecords];
          return updatedRecords.sort((a, b) => a.hrid.localeCompare(b.hrid));
        });
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setError((prevErrors) => [...prevErrors, error.message]);
      });
    setIsLoading(false);
  };
  const handleItemsUpdate = async (id: string) => {
    try {
      setIsLoading(true);
      // Fetch item details
      const response = await fetch(
        `https://api01-v2-uae.ils.medad.com/inventory/items/${id}`,
        { headers }
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const itemsRecords: Item = await response.json();
      setItemDetails((prevRecords) => [...prevRecords, itemsRecords]);

      const edited = itemDetails.find((x) => x.id === id);
      if (edited) {
        itemsRecords.copyNumber = edited.copyNumber;
        itemsRecords.volume = edited.volume;
      }

      // Update the item
      if (valueCallNumber !== "") {
        itemsRecords.callNumber = valueCallNumber;
        itemsRecords.itemLevelCallNumber = valueCallNumber;
        itemsRecords.effectiveCallNumberComponents.callNumber = valueCallNumber;
      }
      if (valueSuffixCallNumber !== "Default") {
        itemsRecords.itemLevelCallNumberSuffix = valueSuffixCallNumber;
        itemsRecords.effectiveCallNumberComponents.suffix =
          valueSuffixCallNumber;
      }
      if (valueSuffixCallNumber === "Empty") {
        itemsRecords.itemLevelCallNumberSuffix = "";
        itemsRecords.effectiveCallNumberComponents.suffix = "";
      }
      holdingDetails.map((holding: any) => {
        //Update Item Location
        if (holding.id === itemsRecords.holdingsRecordId) {
          locations.map((location: any) => {
            if (location.id === holding.permanentLocationId) {
              itemsRecords.permanentLocation = {
                id: location.id,
                name: location.name,
              };
              itemsRecords.effectiveLocation = {
                id: location.id,
                name: location.name,
              };
            }
          });
          //Update Item Material Type
          localMaterialType.map((localMaterialType: LocalMaterialType) => {
            console.log(localMaterialType);
            if (localMaterialType.holdingID === holding.id) {
              itemsRecords.materialType = {
                id: localMaterialType.mTypeID,
                name: localMaterialType.mTypeName,
              };
            }
          });
        }
      });

      const typeId = "0f608771-d6d7-4d04-a143-f52f0b21db24";
      const relevantNotes = itemsRecords.notes.filter(
        (note) => note.itemNoteTypeId === typeId
      );
      if (relevantNotes.length === 0) {
        // addMissingNote(itemsRecords.id, typeId);
        itemsRecords.notes.push({
          itemNoteTypeId: typeId,
          note: "0",
          staffOnly: true,
        });
      }
      valueNotes.map((nt: NoteList) => {
        if (nt.itemId === itemsRecords.id) {
          itemsRecords.notes.map((note) => {
            if (note.itemNoteTypeId === nt.noteTypeId) {
              note.note = nt.note;
            }
          });
        }
      });

      const updateResponse = await fetch(
        `https://api01-v2-uae.ils.medad.com/inventory/items/${itemsRecords.id}`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify(itemsRecords),
        }
      );

      if (!updateResponse.ok) {
        throw new Error(
          "Failed to update the item with ID: " + itemsRecords.id
        );
      }
    } catch (error) {
      setError((prevErrors) => [...prevErrors, "Error updating Items"]);
    }
    setIsLoading(false);
  };

  const handleUpdate = async () => {
    setIsLoading(true);
    try {
      setHoldingDetails([]);
      await Promise.all(
        holdingData.map((holding: any) => {
          handleHoldingDetails(holding.id);
        })
      );
    } catch (error) {}

    //Update Holdings
    try {
      await Promise.all(
        holdingDetails.map(async (holding) => {
          if (valueCallNumber !== "") {
            holding.callNumber = valueCallNumber;
          }
          if (valueSuffixCallNumber !== "Default") {
            holding.callNumberSuffix = valueSuffixCallNumber;
          }
          if (valueSuffixCallNumber === "Empty") {
            holding.callNumberSuffix = "";
          }
          const response = await fetch(
            `https://api01-v2-uae.ils.medad.com/holdings-storage/holdings/${holding.id}`,
            {
              method: "PUT",
              headers,
              body: JSON.stringify(holding),
            }
          );

          if (!response.ok) {
            throw new Error(
              "Failed to update the holding with ID: " + holding.id
            );
          }
        })
      );
    } catch (error) {
      setError((prevErrors) => [...prevErrors, "Error updating holdings"]);
    }

    try {
      setHoldingDetails([]);
      await Promise.all(
        holdingData.map((holding: any) => {
          handleHoldingDetails(holding.id);
        })
      );
    } catch (error) {}

    //Get Items full record & Update Items
    try {
      setItemDetails([]);
      await Promise.all(
        itemData.map((item: any) => {
          handleItemsUpdate(item.id);
        })
      );
      setValueNotes([]);
    } catch (error) {}
    setIsLoading(false);
  };

  useEffect(() => {
    const typeId = "0f608771-d6d7-4d04-a143-f52f0b21db24";
    itemDetails.forEach((item) => {
      const relevantNotes = item.notes.filter(
        (note) => note.itemNoteTypeId === typeId
      );
      if (relevantNotes.length === 0) {
        addMissingNote(item.id, typeId);
      }
    });
  }, [itemDetails]);

  const handleNoteInputChange =
    (itemId: string, noteTypeId: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleNoteChange(itemId, noteTypeId, e.target.value);
    };

  const handleItemFieldChange =
  (itemId: string, field: "copyNumber" | "volume") =>
  (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    setItemDetails((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, [field]: newValue } : item
      )
    );
  };

  const handleNoteChange = (
    itemId: string,
    noteTypeId: string,
    newValue: string
  ) => {
    setItemDetails((prevItems) =>
      prevItems.map((item) => {
        if (item.id === itemId) {
          // Creating a new array of notes with the updated value for the specified note type
          const updatedNotes = item.notes.map((note) => {
            if (note.itemNoteTypeId === noteTypeId) {
              return { ...note, note: newValue }; // Update the note with new value
            }
            return note; // Return other notes as is
          });
          return { ...item, notes: updatedNotes }; // Return a new item object with updated notes
        }
        return item; // Return other items as is
      })
    );
    const newNote: NoteList = {
      itemId: itemId,
      noteTypeId: noteTypeId,
      note: newValue,
    };
    setValueNotes((prevNotes) => [...prevNotes, newNote]);
  };

  const addMissingNote = (itemId: string, typeId: string) => {
    setItemDetails((prevItems) =>
      prevItems.map((item) => {
        if (item.id === itemId) {
          const hasType = item.notes.some(
            (note) => note.itemNoteTypeId === typeId
          );
          if (!hasType) {
            item.notes.push({
              itemNoteTypeId: typeId,
              note: "0", // Default note value
              staffOnly: false, // Assuming default for new notes
            });
          }
        }
        return item;
      })
    );
  };

  const fetchLocations = async () => {
    try {
      const response = await fetch(
        "https://api01-v2-uae.ils.medad.com/locations?limit=5000",
        { headers }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch locations");
      }
      const data = await response.json();
      setLocations(data.locations || []);
    } catch (error) {
      console.error("Error fetching locations:", error);
      // Handle errors as needed
    }
  };

  const fetchMTypes = async () => {
    try {
      const response = await fetch(
        "https://api01-v2-uae.ils.medad.com/material-types?query=cql.allRecords=1 sortby name&limit=2000",
        { headers }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch Material Types");
      }
      const data = await response.json();
      setMTypes(data.mtypes || []);
    } catch (error) {
      console.error("Error fetching Material Types:", error);
      // Handle errors as needed
    }
  };

  const handleSelectChange = (
    holdingID: string,
    mTypeID: string,
    mTypeName: string
  ) => {
    setLocalMaterialType((prevData) => {
      // Check if holdingID exists in the array
      const existingIndex = prevData.findIndex(
        (item) => item.holdingID === holdingID
      );

      if (existingIndex !== -1) {
        // Update the existing object
        const updatedData = [...prevData];
        updatedData[existingIndex].mTypeID = mTypeID;
        updatedData[existingIndex].mTypeName = mTypeName;
        return updatedData;
      } else {
        // Add a new object
        return [...prevData, { holdingID, mTypeID, mTypeName }];
      }
    });
  };

  return (
    <div
      className="container mt-4 themed-container"
      style={{ maxWidth: "90%" }}
    >
      <center>
        {/* <h4>DCT Medad Global Update</h4> */}
        {/* <hr /> */}
      </center>
      {isLoading && <LoadingOverlay />}
      <div className="row alert alert-primary">
        <div className="col-sm-7">
          <label htmlFor="token" className="form-label mb-2">
            Enter Medad Token{" "}
            <a
              href="https://dct.ils.medad.com/settings/developer/token"
              target="_blank"
            >
              Find here
            </a>
          </label>
          <input
            className="form-control"
            id="token"
            type="text"
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
        </div>
        <div className="col-sm-4">
          <label htmlFor="holdingHRID" className="form-label mb-2">
            Enter Instance HRID
          </label>
          <input
            className="form-control"
            id="holdingHRID"
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
        <div
          className="col-sm-1"
          style={{ display: "flex", alignItems: "center" }}
        >
          <button
            id="checkbtn"
            className="btn btn-primary mt-2"
            onClick={handleCheck}
          >
            Check
          </button>
        </div>
      </div>
      <div className="row">
        {instanceData.length > 0 && (
          <div className="col-sm-12  alert alert-success">
            <h5>Title: </h5>
            <h6>{instanceData.map((instance, index) => instance.title)}</h6>
          </div>
        )}
        {holdingDetails.length > 0 && (
          <div className="col-sm-12 alert alert-dark">
            <div className="row ">
              <div className="col-sm-3">
                <h5>Holdings Details:</h5>
              </div>
              <div className="col-sm-9">
                <h5>Items Details:</h5>
              </div>
            </div>

            {holdingDetails.map((holding, index) => (
              <div
                className={
                  index % 2 === 0
                    ? "row alert alert-secondary"
                    : "row alert alert-light"
                }
              >
                <div className="col-sm-3">
                  <strong>Holding Record #{index + 1}</strong>
                  <div className="col-sm-12">
                    <strong>HRID: </strong>
                    {holding.hrid}
                  </div>
                  <div className="col-sm-12">
                    <strong>Call Number: </strong>
                    <span className="badge text-bg-primary ">
                      {holding.callNumber}
                    </span>
                  </div>
                  <div className="col-sm-12">
                    <strong>Suffix: </strong>
                    <span className="badge text-bg-info ">
                      {holding.callNumberSuffix}
                    </span>
                  </div>
                  <div
                    className="col-sm-12"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <strong style={{ marginRight: "5px" }}>Location: </strong>
                    <select
                      className="form-control"
                      // style={{ width: "auto", flexGrow: 1 }}
                      value={holding.permanentLocationId}
                      onChange={(e) => {
                        const updatedHoldings = holdingDetails.map((h) => {
                          if (h.id === holding.id) {
                            return {
                              ...h,
                              permanentLocationId: e.target.value,
                            };
                          }
                          return h;
                        });
                        setHoldingDetails(updatedHoldings);
                      }}
                    >
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div
                    className="col-sm-12"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <strong style={{ marginRight: "5px" }}>
                      Material Type:
                    </strong>
                    <select
                      className="form-control"
                      onChange={(e) =>
                        handleSelectChange(
                          holding.id,
                          e.target.value,
                          e.target.options[e.target.selectedIndex].text
                        )
                      }
                    >
                      <option key="0" value="Default">
                        *Default* for not change value
                      </option>
                      {mTypes.map((mType) => (
                        <option key={mType.id} value={mType.id}>
                          {mType.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-sm-9">
                  <h6>Holding Record #{index + 1} Items List:</h6>
                  {itemData.length > 0 && (
                    <div className="col-sm-12">
                      <div className="row">
                        {(() => {
                          const filteredItems = itemDetails.filter(
                            (item) => item.holdingsRecordId === holding.id
                          );
                          if (filteredItems.length > 0) {
                            return (
                              <>
                                <div className="col-sm-1">
                                  <strong>#</strong>
                                </div>
                                <div className="col-sm-2">
                                  <strong>HRID</strong>
                                </div>
                                <div className="col-sm-1">
                                  <strong>Barcode</strong>
                                </div>
                                <div className="col-sm-2">
                                  <strong>location</strong>
                                </div>
                                <div className="col-sm-2">
                                  <strong>Call Number</strong>
                                </div>
                                <div className="col-sm-1">
                                  <strong>Suffix</strong>
                                </div>
                                <div className="col-sm-1">
                                  <strong>Price</strong>
                                </div>
                                <div className="col-sm-1">
                                  <strong>CP</strong>
                                </div>
                                <div className="col-sm-1">
                                  <strong>V</strong>
                                </div>
                              </>
                            );
                          }
                        })()}

                        {(() => {
                          const filteredItems = itemDetails.filter(
                            (item) => item.holdingsRecordId === holding.id
                          );
                          if (filteredItems.length === 0) {
                            return (
                              <div className="col-sm-12">
                                <strong>No items found.</strong>
                              </div>
                            );
                          }
                          return filteredItems.map((item, index) => (
                            <>
                              <div className="col-sm-1">{index + 1}</div>
                              <div className="col-sm-2">{item.hrid}</div>
                              <div className="col-sm-1">{item.barcode}</div>
                              <div className="col-sm-2">
                                {item.effectiveLocation.name}
                              </div>
                              <div className="col-sm-2">
                                <span className="badge text-bg-primary ">
                                  {item.itemLevelCallNumber}
                                </span>
                              </div>
                              <div className="col-sm-1">
                                <span className="badge text-bg-info ">
                                  {" "}
                                  {item.itemLevelCallNumberSuffix}
                                </span>
                              </div>
                              <div className="col-sm-1">
                                {item.notes
                                  .filter(
                                    (note) =>
                                      note.itemNoteTypeId ===
                                      "0f608771-d6d7-4d04-a143-f52f0b21db24"
                                  )
                                  .map((note, noteIndex) => (
                                    <input
                                      key={noteIndex}
                                      className="form-control"
                                      type="text"
                                      value={note.note}
                                      onChange={handleNoteInputChange(
                                        item.id,
                                        note.itemNoteTypeId
                                      )}
                                    />
                                  ))}
                              </div>
                              <div className="col-sm-1">
                                <input
                                      className="form-control"
                                      type="text"
                                      value={item.copyNumber}
                                      onChange={handleItemFieldChange(item.id, "copyNumber")}
                                    /></div>
                              <div className="col-sm-1">
                                <input
                                      className="form-control"
                                      type="text"
                                      value={item.volume}
                                      onChange={handleItemFieldChange(item.id, "volume")}
                                    />
                              </div>
                            </>
                          ));
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="col-sm-12">
          {holdingDetails.length > 0 && (
            <div className="row  alert alert-info">
              <div className="col-sm-5">
                Fill to update Call Number & Call Number Suffix for all Holdings
                and Items, if kept as is Call Number will not be effected.
              </div>
              <div className="col-sm-3">
                <label htmlFor="newCallNumber" className="form-label mb-2">
                  Enter New Call Number
                </label>
                <input
                  className="form-control"
                  type="text"
                  id="newCallNumber"
                  value={valueCallNumber}
                  onChange={(e) => setValueCallNumber(e.target.value)}
                />
              </div>
              <div className="col-sm-3">
                <label
                  htmlFor="newCallNumberSuffix"
                  className="form-label mb-2"
                >
                  Enter New Call Number Suffix
                </label>
                <select
                  className="form-control"
                  value={valueSuffixCallNumber}
                  onChange={(e) => setValueSuffixCallNumber(e.target.value)}
                >
                  <option key="0" value="Default">
                    *Default* for not change value
                  </option>
                  <option key="1" value="AV">
                    Audio Visual
                  </option>
                  <option key="2" value="CH">
                    Children books
                  </option>
                  <option key="3" value="GULF">
                    Gulf books
                  </option>
                  <option key="4" value="PER">
                    Periodicals
                  </option>
                  <option key="5" value="RARE">
                    Rare Books
                  </option>
                  <option key="6" value="REF">
                    Reference
                  </option>
                  <option key="7" value="RES">
                    Restricted
                  </option>
                  <option key="8" value="THESES">
                    Theses
                  </option>
                  <option key="9" value="Empty">
                    Remove Suffix
                  </option>
                </select>
              </div>
              <div
                className="col-sm-1"
                style={{ display: "flex", alignItems: "flex-end" }}
              >
                <button className="btn btn-primary mt-2" onClick={handleUpdate}>
                  Update
                </button>
              </div>
            </div>
          )}
        </div>
        {error.length > 0 && (
          <>
            {error.map((er) => (
              <div className="col-sm-12 alert alert-danger">Error: {er}</div>
            ))}
          </>
        )}
        {message !== "" && (
          <div className="col-sm-12 alert alert-warning">{message}</div>
        )}
      </div>
    </div>
  );
};

export default GlobalUpdate;
